import { FC, memo, useEffect, useState } from 'react';
import { RadioGroup, Form } from 'rsuite';
import { RadioCell } from 'components/Rsuite/Radio';

import {
  RoasStrategy,
  SuggestKeywordType,
} from 'core/rtbCampaign/singlePage/keyword';
import { useCampaignStore } from 'store/campaignStore';
import { SelectPattern } from 'core/rtbCampaign/singlePage/common';
import Skeleton from 'react-loading-skeleton';
import { RsInput, RsInputSize } from 'components/Rsuite/Input';
import styles from './targetTypeSelection.module.scss';
import { Goal } from '../../Hooks/useOutboundKeywordForm';

export interface TargetTypeSelectionProps {
  selectPattern: SelectPattern;
  roasStrategy: RoasStrategy;
  roas?: string;
  goal?: Goal;
  onRoasStrategyChange: (v: RoasStrategy) => void;
  onCustomRoasChange: (v: string) => void;
  suggestKeywords: SuggestKeywordType[];
  roasError?: string;
  handleGoalChange: (goal: Goal) => void;
}

export const TargetTypeSelection: FC<TargetTypeSelectionProps> = memo(
  ({
    roasStrategy,
    roas,
    goal,
    onRoasStrategyChange,
    roasError,
    handleGoalChange,
    onCustomRoasChange,
  }) => {
    const { actionType, roasList } = useCampaignStore();
    const [loading, setLoading] = useState(true);

    const customRoasMax = () => {
      const baseValue = roasList[1];
      if (baseValue === '-') return 50;
      const computed =
        Math.round((Number(baseValue) + Number.EPSILON) * 1.5 * 100) / 100;
      return computed > 50 ? 50 : computed;
    };

    const currentGoal =
      roasStrategy === RoasStrategy.CUSTOM_ROAS
        ? 'CUSTOM'
        : ((goal ?? Goal.LINK_CLICKS) as Goal | 'CUSTOM');

    useEffect(() => {
      const timer = setTimeout(() => setLoading(false), 4000);
      return () => clearTimeout(timer);
    }, []);

    const onChangeGoal = (value: Goal | 'CUSTOM') => {
      if (value === 'CUSTOM') {
        onRoasStrategyChange(RoasStrategy.CUSTOM_ROAS);
        // goal 交由自訂輸入框 onChange 時用 roas 推論
      } else if (value === Goal.LINK_CLICKS) {
        // 使用者改選固定目標：切回 HIGH_ROAS，並指定目標
        onRoasStrategyChange(RoasStrategy.HIGH_ROAS);
        handleGoalChange(value as Goal);
      } else if (value === Goal.CONVERSIONS) {
        // 使用者改選固定目標：切回 LOW_ROAS，並指定目標
        onRoasStrategyChange(RoasStrategy.LOW_ROAS);
        handleGoalChange(value as Goal);
      }
    };

    return (
      <RadioGroup
        value={currentGoal}
        onChange={(value) => {
          onChangeGoal(value as Goal);
        }}
      >
        <div className={styles.AutoChoiceRadio}>
          {[
            {
              value: Goal.LINK_CLICKS,
              label: loading ? (
                <Skeleton width={450} />
              ) : (
                <span className='text-[#717273]'>
                  <span className='mr-3 font-bold text-[#414651]'>
                    導流 : 投入產出比設定為
                    <span className='mx-1 font-bold text-[#635DA3]'>
                      {roasList[0]}
                    </span>
                    以上
                  </span>
                  導流導向，轉換表現為輔
                </span>
              ),
            },
            {
              value: Goal.CONVERSIONS,
              label: loading ? (
                <Skeleton width={450} />
              ) : (
                <span className='text-[#717273]'>
                  <span className='mr-3 font-bold text-[#414651]'>
                    轉換 : 投入產出比設定為
                    <span className='mx-1 font-bold text-[#635DA3]'>
                      {roasList[2]}
                    </span>
                    以上
                  </span>
                  轉換導向，出價保守投遞易受限
                </span>
              ),
            },

            {
              value: 'CUSTOM',
              label: loading ? (
                <Skeleton width={450} />
              ) : (
                <span className='text-[#717273]'>
                  <span className='mr- flex items-center font-bold text-[#414651]'>
                    自訂投入產出比設定為{' '}
                    <div className='float-none mx-2 inline-block'>
                      <RsInput
                        size={RsInputSize.SMALL}
                        className='max-w-[86px]'
                        placeholder={`1 ~ ${customRoasMax()}`}
                        value={roas ?? ''}
                        onChange={onCustomRoasChange}
                        disabled={
                          roasStrategy !== RoasStrategy.CUSTOM_ROAS ||
                          actionType === 'edit'
                        }
                        errorMessage={roasError}
                      />
                    </div>{' '}
                    以上
                  </span>
                </span>
              ),
            },
          ].map((item) => (
            <RadioCell
              key={item.value}
              value={item.value}
              className=''
              label={item.label}
              disabled={actionType === 'edit'}
            />
          ))}
          <Form.HelpText className='ml-4 !text-sm'>
            <ul className='mt-3 text-xs leading-5 text-[#717680]'>
              <li>
                系統將依投放策略自動出價、選字與投放版位，建議 2
                週內勿更改以利優化。
              </li>
              <li>活動開跑後，不得修改投放目標</li>
              <li>
                每次進入編輯頁面時，系統會顯示最新預估投入產出比(ROAS)數值，點擊「完成」按鈕，活動即會使用最新投入產出比目標
              </li>
              <li>投入產出比僅供參考，若設定過高可能導致投放落後</li>
            </ul>
          </Form.HelpText>
        </div>
      </RadioGroup>
    );
  },
);
